import { V2_BASE, v2Path } from '../nav';
import { NavSectionView } from '../useV2Nav';

/**
 * The onboarding tour's step list.
 *
 * Pure: it turns the nav model the sidebar already renders into an ordered
 * list of steps, so the tour can never mention a tab the user cannot see
 * (a team member, a locked tier, a staged surface) — the same `useV2Nav()`
 * output decides both. Anchors are `data-tour="…"` attributes set by the
 * shell (`nav-<route id>`, `search`, `page-header`); the overlay looks them
 * up at render time, so a step whose anchor is not on screen degrades to a
 * centred card rather than pointing at nothing.
 */

export type TourStepKind = 'welcome' | 'spotlight' | 'nav' | 'page' | 'done';

export interface TourStep {
  /** Stable id; progress is tracked by id, not index, so the list may reflow. */
  id: string;
  kind: TourStepKind;
  /** `data-tour` value of the element to spotlight. Absent = centred card. */
  target?: string;
  title: string;
  body: string;
  /** Small line under the body — why a tab is locked, or that it unlocks later. */
  note?: string;
  /** Route a `nav` step leads to, or a `page` step lives on. */
  route?: string;
  /** Route id, for analytics and for the page step that follows a nav step. */
  navId?: string;
  /**
   * The spotlighted element stays clickable, and clicking it (which navigates)
   * advances the tour. False = the element is shown but clicks are blocked.
   */
  interactive: boolean;
  /** Preferred tooltip side; the overlay flips it when it would not fit. */
  placement: 'right' | 'bottom' | 'left' | 'top';
  /** Label for the primary button on `done`. */
  ctaLabel?: string;
  ctaRoute?: string;
  /**
   * The anchor may legitimately be absent (a control that only renders once
   * there is data). The overlay then skips the step instead of falling back
   * to a centred card.
   */
  optional?: boolean;
}

/** One in-page control worth pointing at once the tab is open. */
interface FeatureCopy {
  /** `data-tour` value on the control. */
  target: string;
  title: string;
  body: string;
}

interface TabCopy {
  /** One line beside the sidebar item. */
  nav: string;
  /** What is on the page, shown once the tab is open. */
  page: string;
  /** The two controls on the page that matter most, in the order to show them. */
  features?: FeatureCopy[];
}

/**
 * Keyed by route id (`V2_ROUTES[].id`). A route missing here still gets a
 * step — with the generic copy below — so adding a nav item never silently
 * drops it from the tour.
 */
export const TAB_COPY: Record<string, TabCopy> = {
  notifications: {
    nav: 'Alerts for new events at the venues and promoters you follow.',
    page:
      'Follow venues or promoters, then choose which emails you want: new event alerts and ticket change alerts. Popular picks subscribe in one click.',
    features: [
      {
        target: 'notifications-settings',
        title: 'Email alerts',
        body: 'Turn email alerts on here, choose new-event and ticket-change alerts, and set the address they go to.',
      },
      {
        target: 'notifications-browse',
        title: 'Venues and promoters',
        body: "Switch between venues and promoters, search, filter by city, and hit Subscribe. Can't find one? Request it from the same list.",
      },
    ],
  },
  waitlist: {
    nav: 'Sold-out DICE shows with an open waitlist.',
    page:
      'Search sold-out events, pick a ticket type, and join the waitlist on every connected DICE account at once, or choose accounts one by one. Joins run in the background.',
    features: [
      {
        target: 'waitlist-defaults',
        title: 'Join defaults',
        body: 'Set once which DICE accounts a join uses and how many tickets each asks for. Every join on this page starts from these answers.',
      },
      {
        target: 'waitlist-search',
        title: 'Find the show',
        body: 'Search by event, venue or promoter and filter by city. Only ticket types with an open waitlist can be selected.',
      },
    ],
  },
  drops: {
    nav: 'Flash Buy auto-purchases tickets the moment they release.',
    page:
      "Choose which DICE accounts Flash Buy may use and set each one's ticket tier, then open the drop tool.",
  },
  tickets: {
    nav: 'Every ticket across your connected accounts, in one place.',
    page:
      'Filter by platform, status and date, switch between Events and Tickets view, export a CSV, and transfer one ticket or many. Listing for sale starts here too.',
    features: [
      {
        target: 'tickets-view',
        title: 'Events or Tickets',
        body: 'Events groups tickets by show, which is the view for transfers. Tickets lists every single ticket, for listing and exporting.',
      },
      {
        target: 'tickets-search',
        title: 'Search and filters',
        body: 'Search by event, venue, ticket type or platform, then narrow by status and date. This Weekend is a one-click shortcut.',
      },
    ],
  },
  listings: {
    nav: 'Your FlashTickets and CrowdVolt listings side by side.',
    page:
      'Listings grouped by event. Edit prices, and link CrowdVolt listings to the matching FlashTickets ones.',
  },
  transfers: {
    nav: "A log of every transfer you've sent or received.",
    page:
      'Audit who got what and when. Filter by source, and sort problems first to catch anything that failed.',
    features: [
      {
        target: 'transfers-search',
        title: 'Search the log',
        body: 'Type an event name to find every transfer for it.',
      },
      {
        target: 'transfers-filters',
        title: 'Filter and sort',
        body: 'Narrow by source, status or account. Problems first surfaces anything that failed.',
      },
    ],
  },
  accounts: {
    nav: 'Link the DICE, AXS and TAO accounts you sell from. Start here.',
    page:
      'Connect an account, re-login when one needs a refresh, and sync to pull the latest tickets. Everything else in FlashTools reads from these connections.',
    features: [
      {
        target: 'accounts-connect',
        title: 'Connect a platform',
        body: 'Pick DICE, AXS or TAO and sign in. Tickets import automatically once an account is connected.',
      },
      {
        target: 'accounts-sync',
        title: 'Sync all',
        body: 'Pull fresh tickets from every connected account in one go. Each account card also has its own sync and re-login.',
      },
    ],
  },
  'dice-settings': {
    nav: 'Managed DICE accounts with SMS forwarding.',
    page:
      "Create new DICE accounts in a couple of clicks, receive their verification codes here, and manage each account's subscription.",
    features: [
      {
        target: 'dice-create',
        title: 'Create a Dice account',
        body: 'Each new number becomes its own DICE account with SMS forwarding, ready to connect.',
      },
      {
        target: 'dice-sms',
        title: 'SMS inbox',
        body: 'Verification codes and messages sent to your Dice numbers land here.',
      },
    ],
  },
  profile: {
    nav: 'Your account details, plan and usage.',
    page:
      "Edit your name, see what plan you're on and how much of it you've used this period, and download invoices.",
    features: [
      {
        target: 'profile-account',
        title: 'Your details',
        body: 'Edit your name here. Sign out lives here too.',
      },
      {
        target: 'profile-billing',
        title: 'Plan and usage',
        body: "Your subscription, what you've used this period, the upcoming charge and past invoices.",
      },
    ],
  },
  billing: {
    nav: 'Pick or change your plan.',
    page:
      'Compare plans, switch between monthly and annual, and manage your card. Upgrades apply right away.',
    features: [
      {
        target: 'billing-plans',
        title: 'Plans',
        body: 'Core, Pro and Reserve side by side. Pick one to start; you can switch any time.',
      },
      {
        target: 'billing-current',
        title: 'Current plan',
        body: 'Your tier, renewal date and trial status. Manage billing opens the Stripe portal for cards and invoices.',
      },
    ],
  },
  seats: {
    nav: 'Add teammates who work your inventory with you.',
    page:
      'Invite people to send tickets from your account. Every transfer they make is logged under their name.',
    features: [
      {
        target: 'team-add',
        title: 'Add a teammate',
        body: 'Enter their email and a label. They get their own login and can only see, refresh and send your tickets.',
      },
      {
        target: 'team-rules',
        title: 'What they can do',
        body: 'The exact list of what a team member can and cannot touch.',
      },
    ],
  },
  setup: {
    nav: 'Step-by-step setup for CrowdVolt.',
    page: 'Install the FlashTools Connect extension and link CrowdVolt, in order.',
  },
  support: {
    nav: 'Ask for help or report a bug.',
    page:
      'Send us a message, with screenshots if it helps. We reply by email, usually within a business day.',
    features: [
      {
        target: 'support-kind',
        title: 'Help or bug',
        body: 'Pick a request type. The form adapts to it.',
      },
      {
        target: 'support-requests',
        title: 'Your requests',
        body: "Everything you've sent, newest first, with its current status.",
      },
    ],
  },
  help: {
    nav: 'Guides and answers on the help centre.',
    page: '',
  },
};

const GENERIC_COPY: TabCopy = {
  nav: 'Open this tab to see what it does.',
  page: 'Everything on this page is explained in its own header.',
};

export interface BuildTourStepsInput {
  sections: NavSectionView[];
  /** The account holds an active plan or an admin comp, so tabs actually open. */
  hasAccess: boolean;
  /** Absolute paths reachable without a plan (billing, profile, support…). */
  bypassPaths: readonly string[];
}

export const WELCOME_STEP_ID = 'welcome';
export const SEARCH_STEP_ID = 'search';
export const DONE_STEP_ID = 'done';

export function navStepId(navId: string): string {
  return `nav-${navId}`;
}

export function pageStepId(navId: string): string {
  return `page-${navId}`;
}

export function featureStepId(navId: string, target: string): string {
  return `feature-${navId}-${target}`;
}

/**
 * Build the ordered step list for the current user.
 *
 * Each visible tab yields a `nav` step ("click here"). When the tab can
 * actually open — not locked, not an external link, and either the account
 * has a plan or the route is on the billing bypass list — a `page` step
 * follows that explains what is on the page. Otherwise the nav step carries
 * a note saying why it is not opened, and the tour moves on.
 */
export function buildTourSteps(input: BuildTourStepsInput): TourStep[] {
  const { sections, hasAccess, bypassPaths } = input;
  const steps: TourStep[] = [];

  steps.push({
    id: WELCOME_STEP_ID,
    kind: 'welcome',
    title: 'Welcome to FlashTools',
    body: 'A quick walk through every tab and the two controls on each that matter most, so you know where things live before you need them. Stop at any point; you can replay it later from Support.',
    interactive: false,
    placement: 'bottom',
  });

  steps.push({
    id: SEARCH_STEP_ID,
    kind: 'spotlight',
    target: 'search',
    title: 'Jump anywhere',
    body: 'Search opens a command palette that lists every tab you can reach. Press ⌘K (Ctrl+K on Windows) from any screen.',
    interactive: false,
    placement: 'right',
  });

  sections.forEach((section) => {
    section.items.forEach((item) => {
      const copy = TAB_COPY[item.id] ?? GENERIC_COPY;
      const external = !item.path.startsWith(V2_BASE);
      const bypass = bypassPaths.some((p) => item.path.startsWith(p));
      const opens = !item.locked && !external && (hasAccess || bypass);

      let note: string | undefined;
      if (item.locked) {
        note = item.title ?? `${item.label} is included in ${item.tierChip ?? 'a higher plan'}.`;
      } else if (external) {
        note = 'Opens the public help centre, outside the app.';
      } else if (!opens) {
        note = 'Opens once you have chosen a plan.';
      }

      steps.push({
        id: navStepId(item.id),
        kind: 'nav',
        target: `nav-${item.id}`,
        title: item.label,
        body: copy.nav,
        note,
        route: item.path,
        navId: item.id,
        interactive: opens,
        placement: 'right',
      });

      if (opens && copy.page) {
        steps.push({
          id: pageStepId(item.id),
          kind: 'page',
          target: 'page-header',
          title: `This is ${item.label}`,
          body: copy.page,
          route: item.path,
          navId: item.id,
          interactive: false,
          placement: 'bottom',
        });
        (copy.features ?? []).forEach((feature) => {
          steps.push({
            id: featureStepId(item.id, feature.target),
            kind: 'page',
            target: feature.target,
            title: feature.title,
            body: feature.body,
            route: item.path,
            navId: item.id,
            interactive: false,
            placement: 'bottom',
            // Some controls only render with data (Sync all, Current plan,
            // the SMS inbox); a brand-new account skips those silently.
            optional: true,
          });
        });
      }
    });
  });

  const accountsVisible = sections.some((s) => s.items.some((i) => i.id === 'accounts' && !i.locked));
  const done: TourStep = {
    id: DONE_STEP_ID,
    kind: 'done',
    title: "That's the tour",
    body: hasAccess
      ? 'The best first step is connecting the account you sell from. Everything else fills in from there.'
      : 'Pick a plan to unlock the tabs, then connect the account you sell from. Everything else fills in from there.',
    interactive: false,
    placement: 'bottom',
    ctaLabel: hasAccess ? 'Connect an account' : 'Choose a plan',
    ctaRoute: hasAccess && accountsVisible ? v2Path('/accounts') : v2Path('/billing'),
  };
  steps.push(done);

  return steps;
}
